import React, { useEffect, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { observer } from "mobx-react";
import { Link, useHistory } from "react-router-dom";
import { signalsData } from "Stores/signalsData";
import { toasterState } from "Components/common/toaster";
import { confirmationPopupState } from "Components/common/confirmationPopup";
import { faviconUrl } from "Pages/Dashboard/Accounts/accountLogo";
import ContactAvatar from "Pages/Dashboard/contacts/ContactAvatar";
import SignalIcon from "./SignalIcon";
import SignalPicker from "./SignalPicker";
import { AccountLogo } from "./SignalsFeed";
import { NO_PAGE_TEXT, directCompetitors, competitorRow, needsInsights, isFinding, movesOf, trimText, timeAgo, domainOf } from "./signalMeta";
import "../agents/agents.scss";
import "./signals.scss";

// What the wait is for, in the order the server does it (the regen overlay's rotating-status pattern).
export const READING_MESSAGES = [
  "Finding their company page…",
  "Reading their page and what their people post…",
  "Spotting the teams they're building and what they ship…",
  "Working out where they're headed…",
  "Working out what it means for you…",
  "Almost ready…"
];
// The first list load looks every name up, so it can take a while too.
export const LIST_MESSAGES = [
  "Opening your seller profile…",
  "Finding who you compete with…",
  "Looking up each company…",
  "Matching their company pages…",
  "Almost ready…"
];

// While a typed name, website or company page is looked up.
export const LOOKUP_MESSAGES = [
  "Looking them up…",
  "Checking their website…",
  "Matching their company page…",
  "Almost there…"
];
export const LOOKUP_DEBOUNCE_MS = 400;
const UNDO_MS = 7000;

// Toasts render as HTML, so a company name goes in escaped.
const esc = v => String(v || "").replace(/[&<>"']/g, ch => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ch]));

export const countOf = (n, word) => {
  const v = Number(n || 0);
  return `${v.toLocaleString()} ${word}${v === 1 ? "" : "s"}`;
};
const PREFETCH_WORKERS = 2;
// While the server is still looking rows up, ask again every few seconds, for a minute at most.
export const RESOLVE_POLL_MS = 4000;
export const RESOLVE_POLL_MAX_MS = 60000;
const LINKEDIN_SETTINGS = "/settings/account/linkedin";

// "https://www.adapt.io/about" -> "adapt.io"
export { domainOf };

const hasPage = c => !!(c && c.domain && c.linkedinUrl);
// The name, not the domain: a row that is still being looked up gets its domain later.
const keyOf = c => c.name;
const shortDate = v => (v && !isNaN(new Date(v)) ? new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "");
const entryFor = c => (hasPage(c) ? signalsData.competitorInsights[c.domain] : { data: { posts: [], insights: [], reason: "no_linkedin_page" } });

// Per-browser memory of each competitor row (domain -> ms it was last read, or ASKED_EVERY_TIME when the list had
// nothing cached for it last time), so a cold visit starts the reads it will need alongside the list, not after it.
// Only a head start: the list still decides. Rows with neither (0) are left alone.
const READ_AT_KEY = "sg.competitors.readAt";
const DAY_MS = 24 * 60 * 60 * 1000;
export const ASKED_EVERY_TIME = -1;
export const staleDomains = (readAt, now = Date.now()) =>
  Object.keys(readAt || {}).filter(d => readAt[d] === ASKED_EVERY_TIME || (readAt[d] > 0 && now - readAt[d] > DAY_MS));
const loadReadAt = () => {
  try { return JSON.parse(localStorage.getItem(READ_AT_KEY) || "{}") || {}; } catch (e) { return {}; }
};
const saveReadAt = map => {
  try { localStorage.setItem(READ_AT_KEY, JSON.stringify(map)); } catch (e) { /* private mode: no head start next time */ }
};
const timeOf = v => (v && !isNaN(new Date(v).getTime()) ? new Date(v).getTime() : 0);

/** "Loading Adapt.io's competitors…" while a switched-to profile with nothing cached loads. */
export const switchingMessage = name => `Loading ${name ? `${name}'s` : "their"} competitors…`;

const ReadingStatus = ({ messages = READING_MESSAGES, hint = "The first read takes a few seconds. After that it is instant for the day." }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI(n => (n + 1) % messages.length), 2500);
    return () => clearInterval(t);
  }, [messages]);
  return (
    <div className="cp-reading" role="status">
      <div className="cp-reading__bar"><div className="cp-reading__fill" /></div>
      <div key={i} className="cp-reading__msg">{messages[i]}</div>
      {hint && <div className="cp-reading__hint">{hint}</div>}
    </div>
  );
};

/** A company the lookup found: its logo (initial when there is none), name and a quiet line of what it is. */
const CandidateLogo = ({ domain, name }) => {
  const [failed, setFailed] = useState(false);
  const src = domain ? faviconUrl(domain) : "";
  return (
    <span className="sg-logo" aria-hidden="true">
      {src && !failed ? <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} /> : String(name || "?").trim().charAt(0).toUpperCase()}
    </span>
  );
};
const aboutLine = k => [k.domain, k.industry, k.employees ? `${k.employees} employees` : ""].filter(Boolean).join(" · ");

/**
 * Find a company by name, website or LinkedIn company page and pick it. Used to add a competitor and to fix which
 * company a row is. Typing looks up after a short pause (Enter looks up at once); Enter only submits once a company is picked.
 */
export const CompetitorLookup = ({ title, subtitle, initialQuery = "", actionLabel, busyLabel, onPick, onClose }) => {
  const [query, setQuery] = useState(initialQuery);
  const [state, setState] = useState({ loading: false, candidates: null, error: "" });
  const [picked, setPicked] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const seq = useRef(0);
  const inputRef = useRef(null);

  const run = async q => {
    const mine = ++seq.current;
    if (!q.trim()) {
      setState({ loading: false, candidates: null, error: "" });
      return;
    }
    setState(s => ({ ...s, loading: true, error: "" }));
    const out = await signalsData.lookupCompetitors(q.trim());
    if (mine !== seq.current) return; // a newer search is on its way
    setState({ loading: false, candidates: out.candidates || [], error: out.error || "" });
  };
  useEffect(() => {
    const t = setTimeout(() => run(query), LOOKUP_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [query]); // eslint-disable-line
  useEffect(() => {
    if (inputRef.current) inputRef.current.focus();
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = async () => {
    if (!picked || saving) return;
    setSaving(true);
    setSaveError("");
    const out = await onPick(picked);
    setSaving(false);
    if (out && out.error) setSaveError(out.error);
  };
  const onInputKey = e => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (picked) submit();
    else run(query);
  };
  const keyOfCandidate = k => `${k.domain || ""}|${k.linkedinUrl || ""}|${k.name}`;
  const { loading, candidates, error } = state;

  let list = null;
  if (loading) list = <ReadingStatus messages={LOOKUP_MESSAGES} hint="" />;
  else if (error) list = <div className="sg-error">{error}</div>;
  else if (candidates && !candidates.length) {
    list = (
      <div className="cp-state cp-lookup__none">
        <b>No company found for "{query.trim()}"</b>
        <p>Paste their website, like acme.com, or their LinkedIn company page.</p>
      </div>
    );
  } else if (candidates) {
    list = (
      <div className="cp-lookup__list" role="listbox" aria-label="Companies found">
        {candidates.map(k => {
          const on = picked && keyOfCandidate(picked) === keyOfCandidate(k);
          return (
            <button key={keyOfCandidate(k)} type="button" role="option" aria-selected={!!on}
              className={`cp-cand ${on ? "on" : ""}`} onClick={() => { setPicked(k); setSaveError(""); }}>
              <CandidateLogo domain={k.logoDomain || k.domain} name={k.name} />
              <span className="cp-cand__text">
                <b>{k.name}</b>
                {aboutLine(k) && <small>{aboutLine(k)}</small>}
              </span>
              <span className="cp-cand__tick material-icons-outlined" aria-hidden="true">{on ? "check_circle" : ""}</span>
            </button>
          );
        })}
      </div>
    );
  }

  return ReactDOM.createPortal(
    <div className="sg-modal-bg" onClick={onClose}>
      <div className="sg-modal cp-lookup" role="dialog" aria-modal="true" aria-label={title} onClick={e => e.stopPropagation()}>
        <div className="sg-modal__head">
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button className="sg-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sg-modal__body">
          <input
            ref={inputRef}
            className="sg-input"
            value={query}
            placeholder="Name, website or LinkedIn page"
            aria-label="Name, website or LinkedIn page"
            onChange={e => { setQuery(e.target.value); setPicked(null); }}
            onKeyDown={onInputKey}
          />
          <div className="cp-lookup__results">{list}</div>
          {saveError && <div className="sg-error">{saveError}</div>}
        </div>
        <div className="sg-modal__foot">
          <button className="sg-btn sg-btn--ghost" onClick={onClose}>Cancel</button>
          <button className="sg-btn" disabled={!picked || saving} onClick={submit}>{saving ? busyLabel : actionLabel}</button>
        </div>
      </div>
    </div>,
    document.body
  );
};

/** The row's ⋯ menu, under its button inside the row (the table doesn't clip, see .cp-table), like the agent card's. */
const RowMenu = ({ onFix, onRemove }) => {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (!(boxRef.current && e && boxRef.current.contains(e.target))) setOpen(false); };
    const onKey = e => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);
  const stop = e => e.stopPropagation();
  const act = fn => e => { e.stopPropagation(); setOpen(false); fn(); };
  return (
    <div ref={boxRef} className="cp-row__more" onClick={stop} onKeyDown={stop}>
      <button className="cp-more" aria-label="More actions" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <i className="material-icons-outlined" aria-hidden="true">more_horiz</i>
      </button>
      {open && (
        <div role="menu" className="ag-menu cp-menu">
          <button role="menuitem" className="ag-menu__item" onClick={act(onFix)}><i className="material-icons-outlined">swap_horiz</i>Not the right company</button>
          <button role="menuitem" className="ag-menu__item ag-menu__item--danger" onClick={act(onRemove)}><i className="material-icons-outlined">delete_outline</i>Remove</button>
        </div>
      )}
    </div>
  );
};

const CompetitorRow = observer(({ c, onOpen, open, gaveUp, onFix, onRemove }) => {
  const r = competitorRow(c, hasPage(c) ? signalsData.competitorInsights[c.domain] : null);
  const text = isFinding(c) && gaveUp ? "Still finding their company page" : r.text;
  return (
    <div
      className={`cp-row ${open ? "is-open" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(c)}
      onKeyDown={e => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen(c))}
    >
      <div className="cp-row__who">
        <AccountLogo key={c.domain || ""} domain={c.domain} name={c.name} />
        <span className="cp-row__name">{c.name}</span>
        {c.source === "user" && <span className="cp-row__by">Added by you</span>}
      </div>
      <div className={`cp-row__latest ${r.quiet ? "is-quiet" : ""}`}>
        {r.pending && !gaveUp ? <span className="cp-row__pending">{text}</span> : text}
      </div>
      <div className="cp-row__n">{r.posts == null ? "–" : r.posts}</div>
      <div className="cp-row__when">{r.updatedAt ? timeAgo(r.updatedAt) : "–"}</div>
      <RowMenu onFix={() => onFix(c)} onRemove={() => onRemove(c)} />
    </div>
  );
});

/** One post behind a move: who and when, a line of what it said, a link out. */
const Evidence = ({ e, post }) => {
  const author = e.author || (post && post.author) || null;
  return (
    <div className={`cp-post ${author ? "cp-post--person" : ""}`}>
      {author ? (
        <div className="cp-post__author">
          <ContactAvatar photo={post && post.author && post.author.photoUrl} name={author.name} />
          <span className="cp-post__who">
            <b>{author.name || "Someone at the company"}</b>
            {author.title && <span>{author.title}</span>}
          </span>
        </div>
      ) : (
        <div className="cp-post__who"><b>Their company page</b></div>
      )}
      <div className="cp-post__meta">
        <span>{shortDate(e.postedAt)}</span>
        {post && <span>{countOf(post.reactions, "reaction")} · {countOf(post.comments, "comment")}</span>}
      </div>
      {post && post.text && <p>{trimText(post.text, 160)}</p>}
      {e.url && <a className="sg-link cp-post__open" href={e.url} target="_blank" rel="noopener noreferrer">Open post <SignalIcon id="external" size={12} /></a>}
    </div>
  );
};

/** One move: what they are doing, what it means for you, and a quiet "From N posts" that opens the posts behind it. */
const Move = ({ m, postsByUrl }) => {
  const [open, setOpen] = useState(false);
  const evidence = (m.evidence || []).filter(e => e && e.url);
  return (
    <div className="cp-insight">
      <b>{m.headline}</b>
      {m.soWhat && <p><span>What it means for you:</span> {m.soWhat}</p>}
      {evidence.length > 0 && (
        <>
          <button type="button" className="cp-evidence__toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
            From {countOf(evidence.length, "post")}
            <i className="material-icons-outlined" aria-hidden="true">{open ? "expand_less" : "expand_more"}</i>
          </button>
          {open && (
            <div className="cp-evidence">
              {evidence.map((e, n) => <Evidence key={e.url || n} e={e} post={postsByUrl[e.url]} />)}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const Detail = observer(({ c, onClose, onRetry }) => {
  useEffect(() => {
    const onKey = e => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  // Opened while it was still being looked up: read its posts once the list has found its page.
  useEffect(() => { if (hasPage(c)) signalsData.fetchCompetitorInsights(c.domain); }, [c.domain, c.linkedinUrl]); // eslint-disable-line
  const entry = entryFor(c);
  const data = (entry && entry.data) || {};
  const reason = data.reason;
  const posts = data.posts || [];
  const peoplePosts = (data.peoplePosts || []).filter(Boolean);
  const groups = movesOf(data);
  const postsByUrl = [...posts, ...peoplePosts].reduce((m, p) => (p && p.url ? { ...m, [p.url]: p } : m), {});

  let body;
  if (isFinding(c)) {
    body = (
      <div className="cp-state">
        <b>Finding their company page…</b>
        <p>We are looking them up from your seller profile. What they're doing shows here as soon as we find them.</p>
      </div>
    );
  } else if (!entry || entry.loading) {
    body = <ReadingStatus />;
  } else if (reason === "no_linkedin_page") {
    body = (
      <div className="cp-state">
        <b>{NO_PAGE_TEXT}</b>
      </div>
    );
  } else if (reason === "no_linkedin_account") {
    body = (
      <div className="cp-state">
        <b>Connect a LinkedIn account in Settings to see what your competitors are doing</b>
        <p>We read their company page and their people's posts through your connected account. Nothing is posted or sent.</p>
        <Link className="sg-btn sg-btn--small" to={LINKEDIN_SETTINGS}>Connect LinkedIn</Link>
      </div>
    );
  } else if (reason === "unavailable") {
    body = (
      <div className="cp-state">
        <b>Couldn't read what they're doing right now.</b>
        <button className="sg-btn sg-btn--small" onClick={() => onRetry(c)}>Try again</button>
      </div>
    );
  } else if (!posts.length && !peoplePosts.length) {
    body = (
      <div className="cp-state">
        <b>Nothing from them in the last 30 days</b>
        <p>When they hire, ship, win a customer or change direction, it shows up here.</p>
      </div>
    );
  } else {
    const read = countOf(posts.length + peoplePosts.length, "post");
    body = groups.length ? (
      groups.map(g => (
        <section key={g.kind} className="cp-moves" aria-label={g.label}>
          <h4 className="cp-h4">{g.label}</h4>
          {g.items.map((m, n) => <Move key={`${g.kind}${n}`} m={m} postsByUrl={postsByUrl} />)}
        </section>
      ))
    ) : data.insightsReason === "unavailable" ? (
      <div className="cp-state">
        <b>Couldn't work out their moves right now</b>
        <p>We read {read} from their page and their people. Try again in a minute.</p>
        <button className="sg-btn sg-btn--small" onClick={() => onRetry(c)}>Try again</button>
      </div>
    ) : (
      <div className="cp-state">
        <b>No clear move in the last 30 days</b>
        <p>We read {read} from their page and their people: nothing about new teams, products, customers, leaders or markets.</p>
      </div>
    );
  }

  // Portalled to the body so the backdrop covers the right rail too (.mainContent is a containing block).
  return ReactDOM.createPortal(
    <div className="sg-drawer-bg" onClick={onClose}>
      <div className="sg-drawer" role="dialog" aria-modal="true" aria-label={c.name} onClick={e => e.stopPropagation()}>
        <div className="sg-drawer__head">
          <AccountLogo domain={c.domain} name={c.name} />
          <div className="sg-drawer__title">
            <h2>{c.name}</h2>
            {c.domain && <p className="cp-domain">{c.domain}</p>}
          </div>
          <button className="sg-x" onClick={onClose} aria-label="Close">×</button>
        </div>
        <div className="sg-drawer__body">{body}</div>
        <div className="sg-drawer__foot">
          {data.generatedAt && !reason && <span className="sg-foot-hint">Updated {timeAgo(data.generatedAt)}</span>}
          <button className="sg-btn sg-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>,
    document.body
  );
});

/** Your competitors: who the seller profile says you compete with, and what each is doing (posts are the evidence). Mounted at /signals/competitors. */
const CompetitorsPage = () => {
  const history = useHistory();
  const [openKey, setOpenKey] = useState("");
  const [tried, setTried] = useState(!!signalsData.competitors);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    // Stale reads start with the list (a couple at a time, like the row prefetch below), not after it.
    if (!signalsData.competitors) staleDomains(loadReadAt()).slice(0, PREFETCH_WORKERS).forEach(d => signalsData.fetchCompetitorInsights(d));
    signalsData.fetchCompetitors().then(() => setTried(true));
  }, []);

  const view = signalsData.competitors;
  const watched = directCompetitors(view && view.competitors);

  // Read the watched rows in the background, a couple at a time, so each row fills in on its own.
  useEffect(() => {
    const queue = watched.filter(c => needsInsights(c) && !signalsData.competitorInsights[c.domain]);
    if (!queue.length) return;
    const work = async () => {
      while (queue.length) await signalsData.fetchCompetitorInsights(queue.shift().domain);
    };
    for (let n = 0; n < PREFETCH_WORKERS; n++) work();
  }, [view]); // eslint-disable-line

  // Remember when each row was last read, for the next cold visit's head start.
  useEffect(() => {
    if (!watched.length) return;
    const map = {};
    watched.filter(hasPage).forEach(c => {
      const e = signalsData.competitorInsights[c.domain];
      map[c.domain] = needsInsights(c) ? ASKED_EVERY_TIME : Math.max(timeOf(e && e.data && e.data.generatedAt), timeOf(c.insightsUpdatedAt));
    });
    saveReadAt(map);
  }, [view, signalsData.competitorInsights]); // eslint-disable-line

  const resolving = all => all.some(c => c && c.resolving);
  const [gaveUp, setGaveUp] = useState(false);
  useEffect(() => {
    if (!view || !resolving(watched)) return undefined;
    let ticks = 0;
    let live = true;
    const t = setInterval(async () => {
      ticks += 1;
      if (ticks * RESOLVE_POLL_MS >= RESOLVE_POLL_MAX_MS) clearInterval(t);
      const next = await signalsData.fetchCompetitors({ sellerProfileId: view.sellerProfileId });
      const still = next && resolving(directCompetitors(next.competitors));
      if (!live) return;
      if (!still || ticks * RESOLVE_POLL_MS >= RESOLVE_POLL_MAX_MS) {
        clearInterval(t);
        if (still) setGaveUp(true);
      }
    }, RESOLVE_POLL_MS);
    return () => { live = false; clearInterval(t); };
  }, [!!view && resolving(watched)]); // eslint-disable-line

  const profileLink = view && view.sellerProfileId ? `/settings/seller-profiles/${encodeURIComponent(view.sellerProfileId)}` : "/settings/seller-profiles/new";
  const openRow = c => {
    setOpenKey(keyOf(c));
  };
  const retry = c => signalsData.fetchCompetitorInsights(c.domain, { force: true });
  const profiles = (view && view.profiles) || [];
  const profile = view && profiles.find(p => p.id === view.sellerProfileId);
  const chooseProfile = async id => {
    if (!id || id === view.sellerProfileId) return;
    setOpenKey("");
    setGaveUp(false);
    // A profile seen this session draws from the store at once and refreshes underneath; only a new one shows a wait.
    setSwitching(!signalsData.competitorsByProfile[id]);
    const ok = await signalsData.chooseCompetitorProfile(id);
    setSwitching(false);
    if (!ok) toasterState.setToastMsg("Couldn't switch the profile. Try again.", "failure");
  };
  const opened = watched.find(c => keyOf(c) === openKey);

  // {fix: row} while fixing a row, {} while adding, null when closed.
  const [lookup, setLookup] = useState(null);
  const closeLookup = React.useCallback(() => setLookup(null), []);
  const addPicked = async k => {
    const out = await signalsData.addCompetitor(k);
    if (out.ok) {
      setLookup(null);
      toasterState.setToastMsg(`Added ${esc(k.name)}. Reading what they're up to…`, "success");
    }
    return out;
  };
  const fixRow = async k => {
    const row = lookup && lookup.fix;
    const out = await signalsData.matchCompetitor(row.name, k);
    if (out.ok) {
      setLookup(null);
      toasterState.setToastMsg(`Updated ${esc(row.name)}. Reading what they're up to…`, "success");
    }
    return out;
  };
  // A hidden profile row comes back by name; a row the member added was deleted, so it is added again.
  const undoRemove = async c => {
    const out = c.source === "user"
      ? await signalsData.addCompetitor({ name: c.name, domain: c.domain, linkedinUrl: c.linkedinUrl })
      : await signalsData.restoreCompetitor(c.name);
    toasterState.setToastMsg(out.ok ? `${esc(c.name)} is back` : out.error, out.ok ? "success" : "failure");
  };
  const removeRow = c => {
    confirmationPopupState.setPopupValues({
      content: `Remove ${c.name} from your competitors? You can add them back any time.`,
      actionBtnText: "Remove",
      needCancelBtn: true,
      callback: async () => {
        const out = await signalsData.removeCompetitor(c.name);
        if (!out.ok) return toasterState.setToastMsg(out.error, "failure");
        if (openKey === keyOf(c)) setOpenKey("");
        toasterState.setPopupValues({ showUndoButton: true, callback: () => undoRemove(c) });
        toasterState.setToasterTimeout(UNDO_MS); // long enough to reach Undo; the toast resets it on close
        return toasterState.setToastMsg(`Removed ${esc(c.name)}`, "success");
      }
    });
    confirmationPopupState.setShowPopup(true);
  };

  let content;
  if (switching && profile) {
    content = <ReadingStatus messages={[switchingMessage(profile.name || domainOf(profile.website))]} hint="" />;
  } else if (switching || (!view && (!tried || signalsData.competitorsLoading))) {
    content = <ReadingStatus messages={LIST_MESSAGES} hint="The first look takes a little while. After that it opens straight away." />;
  } else if (!view || view.reason === "unavailable") {
    content = (
      <div className="sg-empty">
        <div className="sg-empty__art cp-art"><SignalIcon id="competitors" /></div>
        <b>Couldn't load your competitors right now</b>
        <button className="sg-btn" onClick={() => signalsData.fetchCompetitors({ sellerProfileId: view && view.sellerProfileId })}>Try again</button>
      </div>
    );
  } else if (!view.hasSellerProfile) {
    content = (
      <div className="sg-empty">
        <div className="sg-empty__art cp-art"><SignalIcon id="competitors" /></div>
        <b>Add your seller profile so we know who you compete with</b>
        <p>Your seller profile lists the companies you go up against. We take it from there.</p>
        <button className="sg-btn" onClick={() => history.push("/settings/seller-profiles/new")}>Add seller profile</button>
      </div>
    );
  } else if (!watched.length) {
    content = (
      <div className="sg-empty">
        <div className="sg-empty__art cp-art"><SignalIcon id="competitors" /></div>
        <b>No competitors yet</b>
        <p>Add the companies you go up against and we track what they build, ship and win for you.</p>
        <div className="cp-empty__actions">
          <button className="sg-btn" onClick={() => setLookup({})}>Add a competitor</button>
          <Link className="sg-link" to={profileLink}>Edit seller profile</Link>
        </div>
      </div>
    );
  } else {
    content = (
      <div className="cp-table">
        <div className="cp-row cp-row--head">
          <span>Competitor</span><span>Top move</span><span>Posts, 30 days</span><span>Updated</span><span aria-hidden="true" />
        </div>
        {watched.map(c => (
          <CompetitorRow key={keyOf(c)} c={c} onOpen={openRow} open={keyOf(c) === openKey} gaveUp={gaveUp}
            onFix={row => setLookup({ fix: row })} onRemove={removeRow} />
        ))}
      </div>
    );
  }

  return (
    <div className="sg-page">
      <nav className="cp-crumbs" aria-label="Breadcrumb">
        <Link to="/signals">Signals</Link><span aria-hidden="true">›</span><span>Competitors</span>
      </nav>
      <div className="sg-head">
        <div>
          <h1>Your competitors</h1>
          {profile ? (
            <p className="cp-of">
              Competitors of <b>{profile.name || domainOf(profile.website) || "your company"}</b> · from your seller profile
            </p>
          ) : (
            <p>From your seller profile. The teams they're building, what they ship and where they're headed.</p>
          )}
          {/* The page never said what it costs; it costs nothing, and people assume otherwise. */}
          <p className="cp-of cp-of--cost"><b>Free</b> · no signal credits. We read their company page and what their people post, and look again every week.</p>
          {profile && view.profileSource === "latest" && profiles.length > 1 && (
            <p className="cp-of__hint">Pick the profile for your own company</p>
          )}
        </div>
        {view && view.hasSellerProfile && (profiles.length > 1 || watched.length > 0) && (
          <div className="sg-head__right">
            {profiles.length > 1 && (
              <SignalPicker
                className="cp-profile-pick"
                searchable
                value={view.sellerProfileId}
                options={profiles.map(p => ({ value: p.id, label: p.name || domainOf(p.website) || "Untitled profile", hint: domainOf(p.website), tag: p.isYourCompany ? "Your company" : "" }))}
                onChange={chooseProfile}
                placeholder="Choose a profile"
              />
            )}
            {(watched.length > 0 || switching) && <Link className="sg-btn sg-btn--ghost" to={profileLink}>Edit seller profile</Link>}
            {!switching && watched.length > 0 && (
              <button className="sg-btn sg-btn--ghost" onClick={() => setLookup({})}>
                <i className="material-icons-outlined cp-add-ico" aria-hidden="true">add</i>Add competitor
              </button>
            )}
          </div>
        )}
      </div>
      {content}
      {opened && <Detail c={opened} onClose={() => setOpenKey("")} onRetry={retry} />}
      {lookup && (
        <CompetitorLookup
          key={lookup.fix ? `fix:${lookup.fix.name}` : "add"}
          title={lookup.fix ? `Which company is ${lookup.fix.name}?` : "Add a competitor"}
          subtitle={lookup.fix ? "Pick the right one and we track it instead." : "We track the teams they build, what they ship and where they're headed, and what it means for you."}
          initialQuery={lookup.fix ? lookup.fix.name : ""}
          actionLabel={lookup.fix ? "Use this company" : "Add"}
          busyLabel={lookup.fix ? "Saving…" : "Adding…"}
          onPick={lookup.fix ? fixRow : addPicked}
          onClose={closeLookup}
        />
      )}
    </div>
  );
};

export default observer(CompetitorsPage);
